import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../../config";
import {
  ROLES_ALTA_USUARIO,
  isCanonicalUserRole,
  getUserRoleLabel,
  getMessagingRoleLabel,
} from "../../../utils/roles";
import "./GestorUsuarios.css";
import GestorUsuarioCursosEditor from "./GestorUsuarioCursosEditor";
import {
  cursosInscritosToGestorRows,
  sameCourseIdSelection,
  uniqueCourseIdsFromGestorRows,
} from "./gestorUsuarioCursosState";
import PasswordVisibilityField from "../../../components/PasswordVisibilityField";
import ResponsiveActionSidebar from "../../../components/ResponsiveActionSidebar";
import ViewportSelect from "../../../components/ViewportSelect";
import { scrollIntoViewSmooth } from "../../../utils/scrollIntoViewSmooth";

const CURSOS_CATALOG_DEFAULT_ERROR =
  "No se pudo cargar el listado de cursos. Revisa la conexión o inténtalo más tarde.";

const COURSE_ACTION_PERMISSIONS = [
  { key: "puedePublicar", label: "Publicar" },
  { key: "puedeActivar", label: "Activar" },
  { key: "puedeDuplicar", label: "Duplicar" },
];

const RESERVED_PERMISSION_KEYS = new Set([
  "permisosMensajes",
  ...COURSE_ACTION_PERMISSIONS.map((permission) => permission.key),
]);

function GestorUsuarios() {
  const { id } = useParams(); // ID del usuario a gestionar
  const navigate = useNavigate();
  const [rol, setRol] = useState("alumno");
  
  // Estados principales
  const [usuario, setUsuario] = useState(null);
  const [originalUsuario, setOriginalUsuario] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [mensajeScrollTick, setMensajeScrollTick] = useState(0);
  const mensajeRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // Estados de datos modificables
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [cursoRows, setCursoRows] = useState([]);
  const [originalCourseIds, setOriginalCourseIds] = useState([]);

  // Estados para contraseñas
  const [nuevaContraseña, setNuevaContraseña] = useState("");
  const [confirmarNuevaContraseña, setConfirmarNuevaContraseña] = useState("");
  const [showAllPasswords, setShowAllPasswords] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estado para permisos modal
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [userPermissions, setUserPermissions] = useState({});
  
  // Cargar permisos al abrir el modal
  const handleOpenPermissions = async () => {
    try {
        const response = await fetch(`${API_URL}/usuarios/${id}/permisos`, {
            credentials: "include"
        });
        if (response.ok) {
            const data = await response.json();
            setUserPermissions(data.permisos || {});
            setShowPermissionsModal(true);
        } else {
            alert("Error al cargar permisos");
        }
    } catch (e) {
        console.error(e);
        alert("Error de conexión");
    }
  };

  // Toggle permiso
  const togglePermission = async (permisoKey) => {
      const currentValue = userPermissions[permisoKey];
      const newValue = !currentValue;

      // Optimistic UI update
      setUserPermissions(prev => ({
          ...prev,
          [permisoKey]: newValue
      }));

      try {
          const response = await fetch(`${API_URL}/usuarios/${id}/permisos`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ permiso: permisoKey, valor: newValue })
          });
          
          if (!response.ok) {
              // Revertir si falla
              setUserPermissions(prev => ({
                ...prev,
                [permisoKey]: currentValue
              }));
              alert("Error al actualizar permiso");
          }
      } catch (e) {
          console.error(e);
           // Revertir si falla
           setUserPermissions(prev => ({
            ...prev,
            [permisoKey]: currentValue
          }));
          alert("Error de conexión al actualizar permiso");
      }
  };

  // Toggle permiso de mensajería (Nuevo)
  const toggleMessagePermission = async (roleKey) => {
      const currentMap = userPermissions.permisosMensajes || {};
      const currentValue = !!currentMap[roleKey];
      const newValue = !currentValue;

      // Optimistic UI update
      setUserPermissions(prev => ({
          ...prev,
          permisosMensajes: {
              ...prev.permisosMensajes,
              [roleKey]: newValue
          }
      }));

      try {
          const response = await fetch(`${API_URL}/usuarios/${id}/permisos`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              // Enviamos la clave nested para que el backend la procese (Mongoose Map update)
              body: JSON.stringify({ permiso: `permisosMensajes.${roleKey}`, valor: newValue })
          });
          
          if (!response.ok) {
              // Revertir
              setUserPermissions(prev => ({
                ...prev,
                permisosMensajes: {
                    ...prev.permisosMensajes,
                    [roleKey]: currentValue
                }
              }));
              alert("Error al actualizar permiso de mensajería");
          }
      } catch (e) {
          console.error(e);
           // Revertir
           setUserPermissions(prev => ({
            ...prev,
            permisosMensajes: {
                ...prev.permisosMensajes,
                [roleKey]: currentValue
            }
          }));
          alert("Error de conexión al actualizar permiso de mensajería");
      }
  };

  const [cursosDisponibles, setCursosDisponibles] = useState([]);
  const [cursosLoadError, setCursosLoadError] = useState("");

  useEffect(() => {
    if (mensajeScrollTick === 0 || !mensaje) {
      return;
    }
    scrollIntoViewSmooth(mensajeRef.current);
  }, [mensaje, mensajeScrollTick]);

  useEffect(() => {
    let cancelled = false;
    const fetchCursosDisponibles = async () => {
      try {
        const response = await fetch(`${API_URL}/cursos`, {
          method: "GET",
          credentials: "include",
        });
        let data = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }
        if (cancelled) return;
        if (!response.ok) {
          setCursosDisponibles([]);
          const fromBody =
            data !== null &&
            typeof data === "object" &&
            typeof data.error === "string" &&
            data.error.trim() !== ""
              ? data.error.trim()
              : "";
          setCursosLoadError(fromBody || CURSOS_CATALOG_DEFAULT_ERROR);
          return;
        }
        setCursosDisponibles(Array.isArray(data) ? data : []);
        setCursosLoadError("");
      } catch (error) {
        if (!cancelled) {
          console.error("Error al obtener cursos disponibles:", error);
          setCursosDisponibles([]);
          setCursosLoadError(CURSOS_CATALOG_DEFAULT_ERROR);
        }
      }
    };
    fetchCursosDisponibles();
    return () => {
      cancelled = true;
    };
  }, []);


  // Obtener datos del usuario
// 📌 Definir la función obtenerUsuario() fuera del useEffect()
const obtenerUsuario = useCallback(async () => {
  try {
    const response = await fetch(`${API_URL}/usuarios/${id}`, {
      method: "GET",
      credentials: "include",
    });
    const datos = await response.json();

    if (response.ok && datos) {
      setUsuario({ ...datos });
      setOriginalUsuario({ ...datos });
      setFechaNacimiento(datos.fechaNacimiento || "");
      setRol(datos.rol || "alumno");

      const cursos = datos.cursosInscritos || [];
      const rows = cursosInscritosToGestorRows(cursos);
      setCursoRows(rows);
      setOriginalCourseIds(uniqueCourseIdsFromGestorRows(rows));

      setMensaje("");
    } else {
      setMensaje(datos.error || "Error al obtener usuario.");
    }
  } catch (error) {
    console.error("Error al obtener usuario:", error);
    setMensaje("Error de conexión con el servidor.");
  } finally {
    setLoading(false);
  }
}, [id]);

  // Verificar ID
  const isValidId = Boolean(id);
  
  // 📌 Llamar a obtenerUsuario() en el useEffect para cargar los datos al montar el componente
  useEffect(() => {
    if (isValidId) obtenerUsuario();
  }, [isValidId, obtenerUsuario]);

  
  if (!isValidId) {
    // Si no hay ID, retornamos la UI de error directamente en el render principal, 
    // pero para mantener hooks consistentes, dejamos que el componente siga (y mostramos error en el return)
  }
  
  // Función para actualizar el estado del usuario según los cambios en los inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUsuario((prev) => ({
      ...prev,
      [name]: value,
    }));
   // Si el campo es teléfono o dirección, actualizar sus estados también
   if (name === "rol") setRol(value);
 };


// Función para guardar cambios (actualizar usuario)
const handleGuardar = async () => {
  setMensaje("");
  try {
    setLoading(true);

    if (!usuario) {
      throw new Error("No se pudo obtener la información del usuario.");
    }

    if (!/^\d{8}[A-Za-z]$/.test(usuario.dni)) {
      throw new Error("El DNI debe tener 8 números seguidos de una letra.");
    }    

    // Validar teléfono antes de continuar
    if (!/^[0-9]{9}$/.test(usuario.telefono)) {
      throw new Error("El teléfono debe contener exactamente 9 números.");
    }

    const wantsPasswordChange = nuevaContraseña.length > 0 || confirmarNuevaContraseña.length > 0;
    if (wantsPasswordChange && !nuevaContraseña) {
      throw new Error("Introduce la nueva contraseña.");
    }
    if (wantsPasswordChange && nuevaContraseña !== confirmarNuevaContraseña) {
      throw new Error("La nueva contraseña y su confirmación deben coincidir.");
    }

    // Formatear fecha de nacimiento
    const fechaFormateada = usuario.fechaNacimiento ? new Date(usuario.fechaNacimiento).toISOString().split("T")[0] : "";

    // Construir objeto de actualización basado en los estados
    const datosActualizados = {
      dni: usuario.dni,
      nombre: usuario.nombre,
      primerApellido: usuario.primerApellido,
      segundoApellido: usuario.segundoApellido,
      direccion: usuario.direccion,
      telefono: usuario.telefono,
      email: usuario.email,
      fechaNacimiento: fechaFormateada, // Corregido 
      rol: rol,
      cursosInscritos: uniqueCourseIdsFromGestorRows(cursoRows),
    };

    // Llamamos a la API para actualizar usuario
    const response = await fetch(`${API_URL}/usuarios/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(datosActualizados),
    });

    const resultado = await response.json();

    if (response.status === 403) {
      throw new Error("No tienes permisos para actualizar este usuario.");
    }

    if (!response.ok) {
      throw new Error(resultado.error || "Error al actualizar usuario.");
    }

// 🔹 Si hay una nueva contraseña, enviamos una solicitud aparte
// Dentro de handleGuardar, después de actualizar el usuario:
if (wantsPasswordChange) {
  const responsePassword = await fetch(`${API_URL}/usuarios/${id}/cambiar-contrasena-admin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ nuevaContraseña }),  // Se envía solo la nueva contraseña
  });
  const resultadoPassword = await responsePassword.json();
  if (!responsePassword.ok) {
    throw new Error(resultadoPassword.error || "Error al cambiar la contraseña.");
  }
}


// Actualizar estado en React con los datos nuevos
await obtenerUsuario();
setMensaje("Perfil actualizado correctamente.");
setMensajeScrollTick((tick) => tick + 1);

// Limpiar las contraseñas
setNuevaContraseña("");
setConfirmarNuevaContraseña("");

} catch (error) {
console.error("❌ Error al guardar datos:", error);
setMensaje(error.message || "Error al actualizar los datos.");
setMensajeScrollTick((tick) => tick + 1);
} finally {
setLoading(false);
}
};


  // Restaurar datos originales
  const handleRetroceder = () => {
    if (!originalUsuario) return;
    setUsuario(originalUsuario);
    setFechaNacimiento(originalUsuario.fechaNacimiento || "");
    setRol(originalUsuario.rol || "");
 
    const cursos = originalUsuario.cursosInscritos || [];
    const rows = cursosInscritosToGestorRows(cursos);
    setCursoRows(rows);
    setOriginalCourseIds(uniqueCourseIdsFromGestorRows(rows));
    setNuevaContraseña("");
    setConfirmarNuevaContraseña("");
    setMensaje("Datos originales restaurados.");
    setMensajeScrollTick((tick) => tick + 1);
  };


  const hasUnsavedCourseChanges = !sameCourseIdSelection(
    originalCourseIds,
    uniqueCourseIdsFromGestorRows(cursoRows),
  );

  const handleSalir = () => {
    const profileChanged = JSON.stringify(usuario) !== JSON.stringify(originalUsuario);
    const passwordChanged = nuevaContraseña.length > 0 || confirmarNuevaContraseña.length > 0;
    if (profileChanged || hasUnsavedCourseChanges || passwordChanged) {
      if (!window.confirm("Tienes cambios sin guardar. ¿Deseas salir sin guardar?")) {
        return;
      }
    }
    navigate(-1);
  };
  
  
  
  

  if (!isValidId) return <p>Error: ID de usuario no válido.</p>;
  if (loading) return <p>⏳ Cargando usuario...</p>;
  if (!usuario) return <p>Error: No se encontraron datos del usuario.</p>;

  return (
    <div className="gus-container">
      
      <h1 className="GestorUsuarios-titulo">Detalles del Usuario</h1>

      <div className="gus-main-container">
        <div className="gus-content">
          {mensaje && (
            <p ref={mensajeRef} className="text-danger" role="status">
              {mensaje}
            </p>
          )}

          <div className="gestor-form">
            <div className="form-group">
              <label>DNI:</label>
              <input
              type="text"
              name="dni"
              className="form-control"
              value={usuario.dni || ""}
              onChange={(e) => {
                const valor = e.target.value.toUpperCase(); // Convertir a mayúscula
                if (valor.length <= 9) {
                  setUsuario((prev) => ({ ...prev, dni: valor }));
                }
              }}
              pattern="\d{8}[A-Za-z]"
              maxLength="9"
              placeholder="12345678A"
             />
            </div>
            <div className="form-group">
              <label>Nombre:</label>
              <input type="text" name="nombre" className="form-control" value={usuario.nombre || ""} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Primer Apellido:</label>
              <input type="text" name="primerApellido" className="form-control" value={usuario.primerApellido || ""} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Segundo Apellido:</label>
              <input type="text" name="segundoApellido" className="form-control" value={usuario.segundoApellido || ""} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Fecha de Nacimiento:</label>
              <input
                type="date"
                name="fechaNacimiento"
                className="form-control"
                value={fechaNacimiento ? new Date(fechaNacimiento).toISOString().split("T")[0] : ""}
                onChange={(e) => {
                  setFechaNacimiento(e.target.value);
                  setUsuario((prev) => ({
                    ...prev,
                    fechaNacimiento: e.target.value,
                  }));
                }}                
              />
            </div>
            <div className="form-group">
              <label>Dirección:</label>
              <input type="text" name="direccion" className="form-control" value={usuario.direccion || ""} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Teléfono:</label>
              <input
                type="text"
                name="telefono"
                className="form-control"
                value={usuario.telefono || ""}
                onChange={(e) => {
                  const soloNumeros = e.target.value.replace(/\D/g, "");
                  if (soloNumeros.length <= 9) {
                    setUsuario((prev) => ({ ...prev, telefono: soloNumeros }));
                  }
                }}
                maxLength="9"
                placeholder="600700800"
              />
            </div>
            <div className="form-group">
              <label>Email:</label>
              <input type="email" name="email" className="form-control" value={usuario.email || ""} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Rol:</label>
              <ViewportSelect
                className="form-control"
                value={rol}
                onChange={(e) => {
                  setRol(e.target.value);
                }}
              >
                {ROLES_ALTA_USUARIO.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
                {rol && !isCanonicalUserRole(rol) && (
                  <option value={rol}>{getUserRoleLabel(rol)}</option>
                )}
              </ViewportSelect>
            </div>
            <div className="form-group">
              <label>Cursos inscritos:</label>
              <GestorUsuarioCursosEditor
                rows={cursoRows}
                setRows={setCursoRows}
                cursosDisponibles={cursosDisponibles}
                loadError={cursosLoadError}
              />
            </div>
            <div className="form-group">
              <label>Contraseñas:</label>
              <div className="passwords-container">
                <PasswordVisibilityField
                  id="gestor-new-password"
                  placeholder="Nueva contraseña (opcional)"
                  value={nuevaContraseña}
                  autoComplete="new-password"
                  onChange={(e) => {
                    setNuevaContraseña(e.target.value);
                    if (!e.target.value) {
                      setConfirmarNuevaContraseña("");
                    }
                  }}
                  visible={showAllPasswords || showNewPassword}
                  onToggleVisible={() => {
                    if (showAllPasswords) setShowAllPasswords(false);
                    else setShowNewPassword((visible) => !visible);
                  }}
                  showLabelKey="resetPassword.showPassword"
                  hideLabelKey="resetPassword.hidePassword"
                  maxLength={128}
                />
                {nuevaContraseña.length > 0 ? (
                  <PasswordVisibilityField
                    id="gestor-confirm-password"
                    placeholder="Confirmar nueva contraseña"
                    value={confirmarNuevaContraseña}
                    autoComplete="new-password"
                    onChange={(e) => setConfirmarNuevaContraseña(e.target.value)}
                    visible={showAllPasswords || showConfirmPassword}
                    onToggleVisible={() => {
                      if (showAllPasswords) setShowAllPasswords(false);
                      else setShowConfirmPassword((visible) => !visible);
                    }}
                    showLabelKey="resetPassword.showConfirm"
                    hideLabelKey="resetPassword.hideConfirm"
                    required
                    maxLength={128}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <ResponsiveActionSidebar
          id="gestor-usuarios-sidebar"
          className="gus-sidebar"
          openClassName="gus-sidebar--open"
          ariaLabel="Acciones de gestión de usuario"
        >
          <h4>Acciones</h4>
          <ul className="gus-sidebar-links">
            <li>
              <button className="gus-sidebar-button" onClick={handleGuardar}>
                Guardar
              </button>
            </li>
            <li>
              <button className="gus-sidebar-button" onClick={() => setShowAllPasswords(!showAllPasswords)}>
                {showAllPasswords ? "Ocultar Contraseñas" : "Mostrar Contraseñas"}
              </button>
            </li>
            <li>
              <button className="gus-sidebar-button" onClick={handleOpenPermissions}>
                Gestionar Permisos
              </button>
            </li>
            <li>
              <button className="gus-sidebar-button" onClick={handleRetroceder}>
                Restaurar
              </button>
            </li>
            <li>
              <button className="gus-sidebar-button" onClick={handleSalir}>
                Salir
              </button>
            </li>
          </ul>
        </ResponsiveActionSidebar>
      </div>

      {/* MODAL DE PERMISOS */}
      {showPermissionsModal && (
        <div className="permissions-modal-backdrop" onClick={() => setShowPermissionsModal(false)}>
          <div className="permissions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="permissions-header">
              <h3>Gestionar Permisos</h3>
              <button className="close-button" onClick={() => setShowPermissionsModal(false)}>
                &times;
              </button>
            </div>
            
            <div className="permissions-list">
              <h4 style={{ color: '#17a398', margin: '0 0 10px 0', borderBottom: '1px solid rgba(23,163,152,0.3)', paddingBottom: '5px' }}>Generales</h4>
              {COURSE_ACTION_PERMISSIONS.map((permission) => (
                <div key={permission.key} className="permission-item">
                  <span className="permission-label">{permission.label}</span>
                  <label className="switch">
                    <input
                      type="checkbox"
                      aria-label={permission.label}
                      checked={!!userPermissions[permission.key]}
                      onChange={() => togglePermission(permission.key)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              ))}
              {Object.keys(userPermissions).filter((permisoKey) => !RESERVED_PERMISSION_KEYS.has(permisoKey)).map((permisoKey) => (
                <div key={permisoKey} className="permission-item">
                  <span className="permission-label">
                    {/* Formatear nombre: 'puedeBorrarUsuarios' -> 'Puede Borrar Usuarios' */}
                    {permisoKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </span>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={!!userPermissions[permisoKey]}
                      onChange={() => togglePermission(permisoKey)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              ))}

              {Object.keys(userPermissions).filter((permisoKey) => !RESERVED_PERMISSION_KEYS.has(permisoKey)).length === 0 && (
                <p style={{color: '#aaa', textAlign: 'center'}}>No hay más permisos generales configurables.</p>
              )}
              
              <h4 style={{ color: '#17a398', margin: '20px 0 10px 0', borderBottom: '1px solid rgba(23,163,152,0.3)', paddingBottom: '5px' }}>Permisos de Mensajería</h4>
              {[
                ...new Set([
                  "director",
                  "instructor",
                  "alumno",
                  ...(userPermissions.permisosMensajes
                    ? Object.keys(userPermissions.permisosMensajes)
                    : []),
                ]),
              ]
                .sort()
                .map((role) => (
                <div key={role} className="permission-item">
                  <span className="permission-label">
                    Mensaje con {getMessagingRoleLabel(role)}
                  </span>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={!!(userPermissions.permisosMensajes && userPermissions.permisosMensajes[role])}
                      onChange={() => toggleMessagePermission(role)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              ))}
            </div>

            <div className="permissions-footer">
              <button className="permissions-close-btn" onClick={() => setShowPermissionsModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default GestorUsuarios;
